import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, CheckCircle2, AlertTriangle, Loader2, Zap, ShieldCheck, DollarSign, TrendingDown, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALUE_PROPS = [
  { icon: TrendingDown, title: "Wasted-spend keywords", desc: "Find the exact keywords burning your budget with low conversion rates." },
  { icon: Target, title: "Missing negatives", desc: "Discover irrelevant search terms your ads are showing on — and silence them." },
  { icon: DollarSign, title: "Quick-win savings", desc: "Get a prioritized list of fixes that typically save 18–34% of monthly ad spend." },
];

const SOCIAL_PROOF = [
  { stat: "$1.2M+", label: "ad spend audited" },
  { stat: "24h", label: "report turnaround" },
  { stat: "31%", label: "avg waste flagged" },
];

export default function FreeGoogleAdsAuditPage() {
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "",
    website: "",
    google_ads_customer_id: "",
    monthly_ad_spend_usd: "",
    notes: "",
  });

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.website.trim()) {
      toast.error("Please enter your email and website.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        website: form.website.trim(),
        google_ads_customer_id: form.google_ads_customer_id.trim() || undefined,
        monthly_ad_spend_usd: form.monthly_ad_spend_usd ? parseInt(form.monthly_ad_spend_usd, 10) : undefined,
        notes: form.notes.trim() || undefined,
      };
      await axios.post(`${API}/public/audit-request`, payload);
      setSubmitted(true);
      toast.success("Audit request received — check your inbox.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100" data-testid="free-audit-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-zinc-800/80 bg-[#09090B]/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="audit-nav-logo">
            <div className="w-7 h-7 bg-[#0055FF] rounded-sm grid place-items-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold text-lg">RankPilot<span className="text-[#0055FF]"> AI</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" className="text-zinc-300 hover:text-white hover:bg-zinc-900 rounded-sm" data-testid="audit-nav-login">
              <Link to="/login">Log in</Link>
            </Button>
            <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="audit-nav-signup">
              <Link to="/register">Start free trial</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-zinc-800/80 overflow-hidden">
        <div className="grain absolute inset-0 opacity-50" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-12 grid lg:grid-cols-2 gap-16 items-start">
          {/* Left column — pitch */}
          <div>
            <Badge variant="outline" className="rounded-sm border-[#10B981]/40 bg-[#10B981]/10 text-[#34D399] px-3 py-1 mb-6" data-testid="audit-badge">
              <Sparkles className="w-3 h-3 mr-1.5" /> 100% FREE · NO CREDIT CARD
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]" data-testid="audit-h1">
              Stop wasting<br/>
              <span className="text-[#0055FF]">Google Ads budget.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed" data-testid="audit-sub">
              Our AI scans your Google Ads account and identifies the keywords, ads, and search terms quietly burning your monthly budget. Get a free audit report in under 24 hours.
            </p>

            <div className="mt-10 space-y-4">
              {VALUE_PROPS.map((v) => (
                <div key={v.title} className="flex gap-4 items-start" data-testid={`audit-value-${v.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="w-10 h-10 shrink-0 rounded-sm bg-[#0055FF]/10 border border-[#0055FF]/30 grid place-items-center">
                    <v.icon className="w-5 h-5 text-[#3377FF]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-100">{v.title}</h3>
                    <p className="text-sm text-zinc-500 mt-0.5">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-6 border-t border-zinc-800/80 flex flex-wrap gap-6">
              {SOCIAL_PROOF.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl text-zinc-100">{s.stat}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-wider mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — form */}
          <div>
            {submitted ? (
              <div className="border border-[#10B981]/40 bg-[#10B981]/5 rounded-sm p-8 text-center" data-testid="audit-submitted">
                <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto mb-4" />
                <h2 className="font-display text-2xl font-semibold tracking-tight">Audit request received</h2>
                <p className="mt-3 text-zinc-400 leading-relaxed">
                  We've sent a confirmation to <span className="text-zinc-200">{form.email}</span>. Your full audit report will arrive within 24 hours.
                </p>
                <div className="mt-6 p-4 rounded-sm bg-zinc-950 border border-zinc-800 text-left">
                  <div className="flex items-center gap-2 text-sm text-zinc-300 font-medium mb-2">
                    <Sparkles className="w-4 h-4 text-[#3377FF]" /> Want results NOW?
                  </div>
                  <p className="text-sm text-zinc-500 leading-relaxed">
                    Skip the wait. Connect your Google Ads account to RankPilot AI and get an instant audit + AI-generated replacement ads ready to push live.
                  </p>
                </div>
                <Button asChild className="mt-6 w-full h-12 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="audit-instant-cta">
                  <Link to="/register?utm_source=lead_magnet">
                    Start free trial — instant audit <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-8 space-y-5" data-testid="audit-form">
                <div>
                  <Label htmlFor="audit-email" className="text-zinc-300 text-sm">Your work email *</Label>
                  <Input
                    id="audit-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="you@company.com"
                    className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11"
                    data-testid="audit-input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="audit-website" className="text-zinc-300 text-sm">Your website *</Label>
                  <Input
                    id="audit-website"
                    required
                    value={form.website}
                    onChange={setField("website")}
                    placeholder="yourcompany.com"
                    className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11"
                    data-testid="audit-input-website"
                  />
                </div>
                <div>
                  <Label htmlFor="audit-cid" className="text-zinc-300 text-sm">Google Ads Customer ID <span className="text-zinc-500">(optional)</span></Label>
                  <Input
                    id="audit-cid"
                    value={form.google_ads_customer_id}
                    onChange={setField("google_ads_customer_id")}
                    placeholder="123-456-7890"
                    className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11"
                    data-testid="audit-input-cid"
                  />
                </div>
                <div>
                  <Label htmlFor="audit-spend" className="text-zinc-300 text-sm">Monthly ad spend <span className="text-zinc-500">(optional)</span></Label>
                  <div className="mt-2 relative">
                    <DollarSign className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <Input
                      id="audit-spend"
                      type="number"
                      value={form.monthly_ad_spend_usd}
                      onChange={setField("monthly_ad_spend_usd")}
                      placeholder="5000"
                      className="bg-zinc-950 border-zinc-800 rounded-sm h-11 pl-9"
                      data-testid="audit-input-spend"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="audit-notes" className="text-zinc-300 text-sm">What's your biggest Google Ads frustration? <span className="text-zinc-500">(optional)</span></Label>
                  <Textarea
                    id="audit-notes"
                    rows={3}
                    value={form.notes}
                    onChange={setField("notes")}
                    placeholder="e.g. CPCs keep rising, conversions dropping, not sure which keywords to pause..."
                    className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm resize-none"
                    data-testid="audit-input-notes"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full h-12 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm font-medium text-base"
                  data-testid="audit-submit-btn"
                >
                  {busy ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <>Get my free audit <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
                <p className="text-xs text-zinc-600 text-center flex items-center justify-center gap-1.5" data-testid="audit-trust">
                  <ShieldCheck className="w-3 h-3" /> We never share your data. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="border-b border-zinc-800/80">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <span className="label-eyebrow">What's in the report</span>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-3" data-testid="audit-what-h2">
              An 8-page audit, no fluff.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Wasted-spend keywords ranked by burn rate ($ per month)",
              "Negative keyword recommendations (with copy-paste lists)",
              "Low-CTR ad copy flagged with AI-generated replacement headlines",
              "Quality Score breakdown by ad group",
              "Search-term report analysis — irrelevant queries triggering your ads",
              "Bid strategy & budget allocation review",
              "Landing-page-to-keyword relevance scoring",
              "Prioritized action plan ranked by estimated $ savings",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 border border-zinc-800/80 bg-zinc-950/40 rounded-sm p-4" data-testid={`audit-feature-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-300 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-800/80 relative overflow-hidden">
        <div className="grain absolute inset-0 opacity-50" />
        <div className="relative max-w-3xl mx-auto px-6 py-20 text-center">
          <AlertTriangle className="w-10 h-10 text-[#F59E0B] mx-auto mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="audit-cta-h2">
            Don't wait 24 hours. Fix it now.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            Connect your Google Ads account to RankPilot AI and our AI runs the full audit instantly — plus generates replacement ads you can push live with one click.
          </p>
          <Button asChild size="lg" className="mt-8 h-14 px-10 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="audit-bottom-cta">
            <Link to="/register?utm_source=lead_magnet_bottom">
              Start free trial <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
          <p className="mt-4 text-xs text-zinc-600">No credit card · 14-day free trial · Cancel anytime</p>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="py-8 text-center text-xs text-zinc-600">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/" className="hover:text-zinc-400">Home</Link>
            <span className="text-zinc-800">·</span>
            <Link to="/privacy" className="hover:text-zinc-400">Privacy</Link>
            <span className="text-zinc-800">·</span>
            <Link to="/terms" className="hover:text-zinc-400">Terms</Link>
            <span className="text-zinc-800">·</span>
            <a href="mailto:support@myrankpilotai.com" className="hover:text-zinc-400">Contact</a>
          </div>
          <div className="text-zinc-700">Oz Tech Hub · 17 Selma Walk, Donnybrook, VIC 3064, Australia · +61 413 227 784</div>
          <div>© 2026 RankPilot AI · All rights reserved</div>
        </div>
      </footer>
    </div>
  );
}
